import * as React from 'react';
import { AppContext } from '../../../App';
import { AppBar, Typography } from '@mui/material';
import ValidationStatusBadge from '../../common/ValidationStatusBadge';
import { MarkDispatchContext, MarkStateContext } from '../../../context/MarkContext';
import { styled } from '@mui/system';

import { IExtraction } from '../../../types/my-types';

import { renderStatusIcon } from '../../extractionBatches/ExtractionBatchRow';

interface PageTabsProps {
  pages: string[];
  docLabel: string;
  extractionId?: string;
  extraction?: IExtraction;
  documentId: string;
}

const AppBarStyled = styled(AppBar)(() => ({
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  overflow: 'hidden',
  '& .MuiTabs-root': {
    minHeight: '30px',
  },
}));

// Wrapper to prevent content below from showing behind rounded corners
const HeaderWrapper = styled('div')({
  position: 'sticky',
  top: 0,
  // Use default app bar layering so MUI Menu/Popover (z-index 1300) can overlay
  zIndex: 1100,
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  backgroundColor: '#F5F6FA',
});

// Two 50% containers for the page bar: left (tabs) and right (selected text)
const DivBarHalfLeft = styled('div')({
  flex: '0 0 30%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const DivBarHalfRight = styled('div')({
  flex: '0 0 70%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  minWidth: 0, // allow child to shrink so overflow can scroll
  overflow: 'hidden',
});

const DivPagesBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minHeight: '30px',
  padding: '2px 8px',
  gap: '8px',
});

const SpanPageLabel = styled('span')({
  fontSize: '12px',
  color: 'black',
});

const DivPageNumbers = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  overflowX: 'auto',
});

const PageNumberButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
  background: selected ? '#ffffff' : 'transparent',
  border: `1px solid ${selected ? 'rgb(102, 187, 169)' : 'transparent'}`,
  padding: '2px 8px',
  minWidth: '24px',
  height: '24px',
  lineHeight: '20px',
  fontSize: '12px',
  borderRadius: '6px',
  cursor: 'pointer',
  color: 'black',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: selected ? 700 : 500,
  transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
  '&:hover': {
    backgroundColor: selected ? '#f6f6f6' : '#f2f2f2',
    borderColor: selected ? 'rgb(102, 187, 169)' : '#d0d0d0',
  },
  '&:focus-visible': {
    outline: '2px solid rgb(102, 187, 169)',
    outlineOffset: 2,
  },
}));

const PageTabs: React.FC<PageTabsProps> = ({
  pages,
  docLabel,
  extractionId,
  extraction,
  documentId,
}) => {
  const { pageIndex, validationSummaryByExtractionId } = React.useContext(MarkStateContext);

  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);
  const { setToast, dataPoolId } = React.useContext(AppContext);

  const handleSelect = (index: number) => {
    dispatch({ type: 'PAGE_INDEX', payload: index });
  };

  const validationResponse = validationSummaryByExtractionId.find(
    (entry) => entry.extractionId === extractionId
  );

  if (!pages) return null;

  if (pages && pageIndex >= pages.length) {
    return null;
  }

  return (
    <HeaderWrapper>
      <AppBarStyled
        position="sticky"
        color="default"
        elevation={0}
        sx={{ boxShadow: 'none', backgroundColor: '#F5F6FA' }}
        data-cy="div-mark-page-bar"
        data-element="div-doc-page-nav-bar"
      >
        <DivPagesBar>
          <DivBarHalfLeft>
            <DivPageNumbers role="tablist">
              {pages?.map((_, index) => (
                <PageNumberButton
                  key={index}
                  selected={pageIndex === index}
                  data-cy={`document-page-tab-${index}`}
                  aria-label={`Go to page ${index + 1}`}
                  aria-current={pageIndex === index ? 'page' : undefined}
                  aria-selected={pageIndex === index ? 'true' : 'false'}
                  role="tab"
                  type="button"
                  onClick={() => handleSelect(index)}
                >
                  {index + 1}
                </PageNumberButton>
              ))}
            </DivPageNumbers>
          </DivBarHalfLeft>

          <DivBarHalfRight>
            <Typography
              data-cy="doc-filename"
              style={{
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                overflowX: 'auto',
                maxWidth: '100%',
              }}
            >
              <ValidationStatusBadge
                validationResponse={validationResponse}
                cypressId={'pageTabs-title'}
              />
              <span
                style={{ marginLeft: '4px', cursor: 'pointer' }}
                onClick={() => {
                  navigator.clipboard.writeText(docLabel || '');
                  setToast({
                    open: true,
                    message: 'Copied!',
                    variant: 'success',
                  });
                }}
              >
                {docLabel}
              </span>
              {extraction
                ? renderStatusIcon(docLabel, extraction?.status, extraction?.data_processing_status)
                : null}
            </Typography>
          </DivBarHalfRight>
        </DivPagesBar>
      </AppBarStyled>
    </HeaderWrapper>
  );
};

export default PageTabs;
